import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { OAuthCredentials } from '../types';
import { getOAuthCredentials, setOAuthCredentials, clearOAuthCredentials, ADK_API_BASE_URL } from '../config/api';

interface OAuthContextType {
  isAuthenticated: boolean;
  userEmail: string | null;
  credentials: OAuthCredentials | null;
  login: (redirectUri: string) => void;
  handleCallback: (code: string, state: string) => Promise<void>;
  /**
   * Take credentials obtained outside this provider and become authenticated.
   *
   * The OAuth callback route exchanges a handoff cookie for the tokens and
   * then routes to `/` with react-router, which does NOT remount this
   * provider — and `checkAuth` only reads sessionStorage on mount. So the
   * first sign-in landed on an app that still had `isAuthenticated === false`
   * and showed the login screen again. The second attempt appeared to work
   * only because the first round's credentials were already in sessionStorage
   * when the page loaded fresh. Hence: sign in twice, every time.
   */
  adoptCredentials: (creds: OAuthCredentials) => void;
  logout: () => void;
  isLoading: boolean;
}

const OAuthContext = createContext<OAuthContextType | undefined>(undefined);

export const useOAuth = (): OAuthContextType => {
  const context = useContext(OAuthContext);
  if (!context) {
    throw new Error('useOAuth must be used within OAuthProvider');
  }
  return context;
};

interface OAuthProviderProps {
  children: React.ReactNode;
}

export const OAuthProvider: React.FC<OAuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [credentials, setCredentials] = useState<OAuthCredentials | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing credentials on mount
  useEffect(() => {
    const checkAuth = () => {
      const creds = getOAuthCredentials();
      const email = sessionStorage.getItem('user_email');

      if (creds && creds.access_token) {
        setCredentials(creds);
        setUserEmail(email);
        setIsAuthenticated(true);
      }

      setIsLoading(false);
    };

    checkAuth();
  }, []);

  /**
   * Redirect to OAuth login page
   * @param redirectUri The redirect URI to use for callback
   */
  const login = (redirectUri: string) => {
    // Build OAuth authorization URL
    const authUrl = `${ADK_API_BASE_URL}/oauth/authorize?redirect_uri=${encodeURIComponent(redirectUri)}`;

    // Redirect to backend OAuth endpoint
    window.location.href = authUrl;
  };

  /**
   * Handle OAuth callback after user authorizes
   * @param code Authorization code from OAuth callback
   * @param state State parameter for CSRF protection
   */
  const handleCallback = async (code: string, state: string) => {
    setIsLoading(true);

    try {
      // Exchange code for tokens via backend
      const response = await fetch(`${ADK_API_BASE_URL}/oauth/callback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code, state })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'OAuth callback failed');
      }

      const data = await response.json();

      // Store credentials and user info
      const creds: OAuthCredentials = {
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        client_id: data.client_id,
        client_secret: data.client_secret,
        email: data.email
      };

      setOAuthCredentials(creds);
      sessionStorage.setItem('user_email', data.email || '');

      setCredentials(creds);
      setUserEmail(data.email);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('OAuth callback error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Adopt credentials fetched elsewhere (the OAuth handoff) into this provider.
   *
   * Writes them through the same storage helper the mount-time check reads, so
   * a later remount agrees with what is in state now.
   */
  // Stable across renders on purpose: the OAuth callback effect lists this
  // in its dependency array, and a new function each render would re-run
  // the handoff POST on every render. The cookie is single-use, so every
  // repeat would 401.
  const adoptCredentials = useCallback((creds: OAuthCredentials) => {
    if (!creds?.access_token) return;
    setOAuthCredentials(creds);
    if (creds.email) {
      sessionStorage.setItem('user_email', creds.email);
    }
    setCredentials(creds);
    setUserEmail(creds.email ?? null);
    setIsAuthenticated(true);
    setIsLoading(false);
  }, []);

  /**
   * Sign out and clear all authentication data
   */
  const logout = () => {
    clearOAuthCredentials();
    setCredentials(null);
    setUserEmail(null);
    setIsAuthenticated(false);

    // Optionally redirect to home
    window.location.href = '/';
  };

  const value: OAuthContextType = {
    isAuthenticated,
    userEmail,
    credentials,
    login,
    handleCallback,
    adoptCredentials,
    logout,
    isLoading
  };

  return <OAuthContext.Provider value={value}>{children}</OAuthContext.Provider>;
};
